import type { SharedModels } from '@boostbrothers/web-api';
import { ClientApiFactory } from '@boostbrothers/web-api/web-gateway-api';
import { SimpleAlertModal } from '@boostbrothers/web-ui/web';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { PA_URL } from '~constants/url';
import useModal from '~hooks/useModal';
import useToast from '~hooks/useToast';
import useViewTransition from '~hooks/useViewTransition';

const useCMSImageUpload = ({
  handleTemporarySave,
  fromPA,
}: {
  handleTemporarySave: () => void;
  fromPA: boolean;
}) => {
  const [imageFile, setImageFile] = useState<File | null>(null);

  const { navigateWithTransition } = useViewTransition();
  const { addToast } = useToast();
  const { openModal, closeModal } = useModal();

  const handleError = (status: number) => {
    if (status === 401) {
      openModal({
        modalContent: fromPA ? (
          <SimpleAlertModal
            title="로그인 세션이 만료되었습니다. PA에서 다시 진입해주세요."
            description={`해당 페이지는 닫히지 않습니다. 필요한 내용을 복사 후, 새로운 탭에서 이어서 작성해주세요.`}
            confirmText="로그인"
            confirmAction={() => {
              closeModal();
              sessionStorage.removeItem('userToken');

              window.open(PA_URL, '_blank');
            }}
          />
        ) : (
          <SimpleAlertModal
            title="로그인 세션이 만료되었습니다."
            description="다시 로그인 하시면 현재 작성된 내용 그대로 이어서 작성할 수 있습니다."
            confirmText="로그인"
            confirmAction={() => {
              closeModal();
              localStorage.removeItem('userToken');
              handleTemporarySave();
              navigateWithTransition('/login');
            }}
          />
        ),
      });
      return;
    }

    addToast({
      title: '저장을 실패했습니다.',
      state: 'error',
      position: 'bottom-center',
    });
  };

  const { mutateAsync: uploadImage, isPending: isUploading } = useMutation<
    string,
    SharedModels.ErrorRes,
    File
  >({
    mutationFn: async (file: File) => {
      // 1. Presigned URL 요청
      const res = await ClientApiFactory('POST /v1/cms/file/images')({
        payload: { filename: file.name },
      });

      // 2. S3 업로드
      const formData = new FormData();
      Object.entries(res.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('Content-Type', file.type);
      formData.append('file', file);

      const response = await fetch(res.uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('이미지 업로드에 실패했습니다.');
      }

      return res.url;
    },
    onSuccess: () => {
      addToast({
        title: '이미지 업로드에 성공했습니다.',
        state: 'success',
        position: 'bottom-center',
      });
    },
    onError: ({ error }) => handleError(error.status),
  });

  return {
    uploadImage,
    isUploading,
    imageFile,
    setImageFile,
  };
};

export default useCMSImageUpload;
